/**
 * Hash PII (Personally Identifiable Information) for ad network matching
 *
 * Follows industry standard (Meta, TikTok, Reddit):
 * - Normalize to E.164 format (+12125551234)
 * - SHA-256 hash
 * - Lowercase hex encoding
 */

/**
 * Hash a phone number for ad network matching
 *
 * @param phoneNumber - Phone number in E.164 format (+12125551234)
 * @returns SHA-256 hash as lowercase hex string
 */
export const hashPhoneNumber = async (phoneNumber: string): Promise<string> => {
  // Normalize: remove all non-digits
  const digits = phoneNumber.replace(/\D/g, "")

  // Ensure E.164 format with + prefix
  const normalized = `+${digits}`

  return sha256Hex(normalized)
}

/**
 * Hash an email address for ad network matching
 *
 * @param email - Raw email address
 * @returns SHA-256 hash as lowercase hex string
 */
export const hashEmail = async (email: string): Promise<string> => {
  // Normalize: trim surrounding whitespace and lowercase (Meta/TikTok standard)
  const normalized = email.trim().toLowerCase()

  return sha256Hex(normalized)
}

const sha256Hex = async (value: string): Promise<string> => {
  const msgBuffer = new TextEncoder().encode(value)
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
}
